import { findActivePilotProgram } from './pilotPrograms';

export const GENDER_AT_BIRTH_PREFILL_OPTIONS = ['Male', 'Female', 'Intersex'] as const;

export const MARITAL_STATUS_PREFILL_OPTIONS = [
  'Never married',
  'Married',
  'Married, spouse absent',
  'Partnered',
  'Separated',
  'Divorced',
  'Widowed',
] as const;

export const PARTNER_QUESTION_MARITAL_STATUSES = ['Married', 'Partnered'] as const;

export function shouldAskPartnerQuestions(maritalStatus: string | undefined | null): boolean {
  if (!maritalStatus) return false;
  return (PARTNER_QUESTION_MARITAL_STATUSES as readonly string[]).includes(maritalStatus);
}

export const FINANCIAL_ASSET_TYPES_PREFILL_OPTIONS = [
  'Checking or savings account',
  'CDs, stocks, bonds, or mutual funds',
  'Individual Retirement Account (IRA), 401K, or other retirement savings vehicles',
  'Pension and/or social security income',
  'Real estate that is your primary residence',
  'Real estate that is NOT your primary residence',
  'Interest or equity in a business or LLC',
  'None of these',
] as const;

export const LEGAL_DOCUMENTS_PREFILL_OPTIONS = [
  'Advance Directive (or Healthcare Directive) / Advance statements',
  'Living Wills / CPR & DNR',
  'Trusts',
  'Durable Healthcare Power of Attorney/ Medical POA',
  'Conservatorship / Durable POA - control of assets / Durable Power of Attorney for Assets',
  'Funeral Arrangements',
  'None of these',
] as const;

export const PROFESSIONAL_SUPPORT_NETWORK_PREFILL_OPTIONS = [
  'Financial advisor',
  'Estate planner',
  'Care coordinator',
  'Insurance broker',
  "I'm not working with anyone",
  'Other',
] as const;

export const HIGHEST_DEGREE_PREFILL_OPTIONS = [
  'No degree',
  'GED',
  'High school diploma',
  'Associates',
  'Bachelors',
  'Masters',
  'Law/MD/PhD',
  'Other',
] as const;

export const PREFILL_SECTIONS: { title: string; questionIds: string[] }[] = [
  {
    title: 'About you',
    questionIds: [
      'clientFirstName',
      'clientLastName',
      'clientEmail',
      'clientBirthDate',
      'clientZipCode',
      'clientGenderAtBirth',
      'clientMaritalStatus',
      'partnerFirstName',
      'partnerLastName',
    ],
  },
  {
    title: 'Household & finances',
    questionIds: [
      'householdHouseholdSize',
      'householdTotalAssetsValue',
      'householdPriorYearIncome',
      'financialAssetTypes',
    ],
  },
  {
    title: 'Planning & support',
    questionIds: ['legalDocuments', 'clientProfessionalSupportNetworkList', 'clientHighestDegree'],
  },
];

export const EXCLUSIVE_PREFILL_CHOICES: Record<string, string[]> = {
  financialAssetTypes: ['None of these'],
  legalDocuments: ['None of these'],
  clientProfessionalSupportNetworkList: ["I'm not working with anyone", 'Other'],
};

export const PREFILL_CURRENCY_QUESTION_IDS = [
  'householdTotalAssetsValue',
  'householdPriorYearIncome',
];

export const PREFILL_FIELD_RULES = {
  clientZipCode: { maxLength: 5 },
  partnerFirstName: { maxLength: 100 },
  partnerLastName: { maxLength: 100 },
  householdHouseholdSize: { min: 1, max: 50, integer: true },
  householdTotalAssetsValue: { min: 0, max: 1_000_000_000, isCurrency: true },
  householdPriorYearIncome: { min: 0, max: 100_000_000, isCurrency: true },
  clientBirthDate: { minYear: 1900 },
} as const;

export const PREFILL_QUESTION_LABELS: Record<string, string> = {
  clientFirstName: 'What is your first name?',
  clientLastName: 'What is your last name?',
  clientEmail: 'What is your preferred email address?',
  partnerFirstName: "What is your partner's first name?",
  partnerLastName: 'What is their last name?',
  clientBirthDate: 'What is your birth date?',
  clientZipCode: 'What is the zip code of your primary residence?',
  clientGenderAtBirth: 'What sex were you assigned at birth?',
  clientMaritalStatus: 'What is your marital status?',
  householdHouseholdSize:
    'How many individuals, including yourself, currently reside in your household?',
  householdTotalAssetsValue:
    'What is your net worth, or the total value of all your assets (like property, investments) minus any debts?',
  householdPriorYearIncome: 'What was your total household income last year?',
  financialAssetTypes: 'Please select all the types of assets you own.',
  legalDocuments: 'Do you have any of the following set up?',
  clientProfessionalSupportNetworkList: 'Are you working with any of the following?',
  clientHighestDegree: 'What is your highest level of education that you have completed?',
};

export const ADVISOR_PREFILL_PREVIEW_METADATA_KEY = 'advisorPrefillPreview';

export const FULL_ADVISOR_PREFILL_QUESTION_IDS = [
  'clientFirstName',
  'clientLastName',
  'clientEmail',
  'partnerFirstName',
  'partnerLastName',
  'clientBirthDate',
  'clientZipCode',
  'clientGenderAtBirth',
  'clientMaritalStatus',
  'householdHouseholdSize',
  'householdTotalAssetsValue',
  'householdPriorYearIncome',
  'financialAssetTypes',
  'legalDocuments',
  'clientProfessionalSupportNetworkList',
  'clientHighestDegree',
] as const;

export const EDJ_ADVISOR_PREFILL_QUESTION_IDS = [
  'clientFirstName',
  'clientLastName',
  'clientEmail',
  'partnerFirstName',
  'partnerLastName',
  'clientBirthDate',
  'clientZipCode',
  'householdHouseholdSize',
  'householdTotalAssetsValue',
  'householdPriorYearIncome',
  'financialAssetTypes',
  'legalDocuments',
] as const;

export type AdvisorPrefillMode = 'disabled' | 'full' | 'edj-restricted';

export interface AdvisorPrefillPolicy {
  enabled: boolean;
  mode: AdvisorPrefillMode;
  allowedQuestionIds: readonly string[];
  canCreateQuickPlan: boolean;
}

/** One policy drives feature availability, field access and quick-plan creation. */
export function resolveAdvisorPrefillPolicy(
  featureFlags: FeatureFlags | null | undefined,
): AdvisorPrefillPolicy {
  if (!featureFlags?.perClientInviteLink) {
    return {
      enabled: false,
      mode: 'disabled',
      allowedQuestionIds: [],
      canCreateQuickPlan: false,
    };
  }

  const isEdjAdvisor = findActivePilotProgram(featureFlags)?.flagKey === 'edjPilotAdvisor';
  if (isEdjAdvisor) {
    return {
      enabled: true,
      mode: 'edj-restricted',
      allowedQuestionIds: EDJ_ADVISOR_PREFILL_QUESTION_IDS,
      canCreateQuickPlan: false,
    };
  }

  return {
    enabled: true,
    mode: 'full',
    allowedQuestionIds: FULL_ADVISOR_PREFILL_QUESTION_IDS,
    canCreateQuickPlan: true,
  };
}

export function isAdvisorPrefillQuestionAllowed(
  policy: AdvisorPrefillPolicy,
  questionId: string,
): boolean {
  return policy.allowedQuestionIds.includes(questionId);
}

export function isPerClientInviteLinkEnabledForAdvisor(
  featureFlags: FeatureFlags | null | undefined,
): boolean {
  return resolveAdvisorPrefillPolicy(featureFlags).enabled;
}

/**
 * The answers a plan cannot be inferred without. Age, sex and location drive the
 * prediction more than anything else, and no default for them is neutral — a guessed
 * birth date or sex invents a plan rather than degrading one, and a guessed zip code
 * prices care in the wrong region. Everything else the algorithm service or our own
 * assumptions can stand in for, so these three are what the advisor must supply for the
 * client to be workable straight away.
 */
export const PLAN_REQUIRED_PREFILL_FIELDS = [
  'clientBirthDate',
  'clientGenderAtBirth',
  'clientZipCode',
] as const;

export const PLAN_REQUIRED_PREFILL_SHORT_LABELS: Record<string, string> = {
  clientBirthDate: 'birth date',
  clientGenderAtBirth: 'sex at birth',
  clientZipCode: 'zip code',
};

export function formatMissingPlanFields(questionIds: readonly string[]): string {
  const names = questionIds.map(id => PLAN_REQUIRED_PREFILL_SHORT_LABELS[id] ?? id);
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}
